import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { TextField as MuiTextField } from '@mui/material'
import type { MyOrganisationDTO } from '@api/fastAPI.schemas'
import { useGetMyOrganisations } from '@api/my-organisation/my-organisation'
import { FormControl } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import { organisationLabel } from '@utils'

/**
 * States which organisation a new project will be created in -- read-only,
 * because that choice belongs to the whole session and is made once in the
 * navbar's organisation switcher, not per project.
 *
 * Rendered only for users who belong to several organisations, mirroring the
 * switcher itself: with a single organisation there is nothing to tell apart.
 */
const ActiveOrganisationField = (): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const activeOrganisationGuid = useAppStore(state => state.activeOrganisationGuid)

  const { data: organisations } = useGetMyOrganisations<MyOrganisationDTO[], AxiosError>({
    query: {
      select: data => data.organisations,
      staleTime: Infinity,
      retry: false,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Organisationen'), {
          variant: 'error',
        })
      },
    },
  })

  if (!organisations || organisations.length < 2) return null

  // Same fallback as the switcher: without a choice the backend creates in the
  // default organisation, which is the first one listed.
  const active =
    organisations.find(organisation => organisation.guid === activeOrganisationGuid) ??
    organisations[0]

  return (
    <FormControl
      label="Organisation"
      tooltip={{
        text: 'Das Projekt wird in dieser Organisation angelegt. Die Organisation lässt sich im Kontomenü der Navigationsleiste unter "Organisation" wechseln.',
      }}
      data-cy="input-organisation"
    >
      <MuiTextField
        value={organisationLabel(active)}
        size="small"
        disabled
        data-cy="input-organisation-value"
      />
    </FormControl>
  )
}

export default ActiveOrganisationField
